'use client';
import React, { useState, useEffect, Fragment, use } from 'react';
import {
  getDigitalWorkplacePageDataBySlug,
  getSectionDataByName,
  isHomePage,
} from '@/libs/sanity/client';
import Header from '@/components/Header';
import SecondaryMenu from '@/components/SecondaryMenu';
import Footer from '@/components/Footer';
import GeneralComponent from '@/components/GeneralComponent';
import GeneralFullWidthComponent, {
  isGeneralFullWidthComponent,
} from '@/components/FullWidthGeneralComponent';
import Sidebar, { sidebarItem } from '@/components/Sidebar';
import { convertToSlug } from '@/utils';
import ModuleCarousel from '@/components/ModuleCarousel';
import SkeletonLoader from '@/components/SkeletonLoader';
import { useRouter } from 'next/navigation';
import { MENU_ITEMS } from '../secondaryMenuItems';
import { useMediaQuery } from 'usehooks-ts';
import { MOBILE_SCREEN_SIZE, TABLET_SCREEN_SIZE } from '@/utils/constants';
import RelatedArticle from '@/components/RelatedArticle';

type DigitalWorkplacePageProps = {
  params: Promise<{
    slug: string;
  }>;
};

const Title = (
  <h3 className="text-lg pb-8 max-w-[80%]">
    Explore Simplicity<sup>®</sup> <br />
    <span className="text-primary drop-shadow-md font-medium">
      Digital Workplace
    </span>
  </h3>
);

function DigitalWorkplacePage(props: DigitalWorkplacePageProps) {
  const params = use(props.params);
  const [data, setData] = useState<any>(null);
  const [moduleData, setModuleData] = useState<any>(null);
  const [sidebarItems, setSidebarItems] = useState<sidebarItem[]>([]);
  const [error, setError] = useState<Error | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const router = useRouter();
  const isTablet = useMediaQuery(`(max-width: ${TABLET_SCREEN_SIZE}px)`);
  const isMobile = useMediaQuery(`(max-width: ${MOBILE_SCREEN_SIZE}px)`);

  const fetchData = async (slug: string) => {
    try {
      const pageData = await getDigitalWorkplacePageDataBySlug(slug);
      if (!pageData) {
        router.push('/404'); // Redirect to 404 page if data not found
        return;
      }
      setData(pageData);
    } catch (err) {
      setError(err as Error);
    }
  };

  const fetchModuleData = async () => {
    try {
      const sectionData = await getSectionDataByName('Simplicity® DW Modules');
      if (!sectionData) throw new Error('Section data not found');
      sectionData.anchor = convertToSlug(sectionData.title);

      const slideItems = sectionData?.modules?.[0]?.slideItems || [];
      for (const item of slideItems) {
        if (item.subTitle && item.title === 'Common Utilities') {
          item.slug = `/digital-workplace/${convertToSlug(item.subTitle)}`;
        } else {
          item.slug = `/digital-workplace/${convertToSlug(item.title)}`;
        }
      }
      setModuleData(sectionData);
    } catch (err) {
      setError(err as Error);
    }
  };

  const generateSidebarItems = (pageData: any) => {
    const filteredSidebarItems: sidebarItem[] = pageData.modules
      .filter(
        (module: any) => module.name && !isGeneralFullWidthComponent(module)
      )
      .map((module: any, index: number) => ({
        id: index,
        content: module.name,
        href: `#${convertToSlug(module.name)}`,
        isActive: index === 0,
        type: module._type,
        hash: convertToSlug(module.name),
      }));

    filteredSidebarItems.push({
      id: filteredSidebarItems.length,
      content: 'Simplicity® DW Modules',
      href: 'simplicity-dw-modules',
      isActive: false,
      type: 'simplicity-dw-modules',
      hash: 'simplicity-dw-modules',
    });

    setSidebarItems(filteredSidebarItems);
  };

  useEffect(() => {
    const slug = params.slug
      ? `/digital-workplace/${params.slug}`
      : '/digital-workplace';
    Promise.all([fetchData(slug), fetchModuleData()])
      .catch((err) => {
        setError(err as Error);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [params.slug]);

  useEffect(() => {
    if (data) {
      generateSidebarItems(data);
    }
  }, [data]);

  if (error) {
    return (
      <div className="error-message">
        Error loading page data: {error.message}
      </div>
    );
  }

  if (loading) {
    return <SkeletonLoader />;
  }

  if (!data || !moduleData) {
    return null;
  }

  const renderedModules = data.modules.map((module: any, index: number) => {
    module.anchor = convertToSlug(module.name);
    return (
      <Fragment key={module._key}>
        <GeneralComponent {...module} />
        {index !== 0 && index < data.modules.length - 1 && (
          <hr className="bg-neutral-200 my-12 h-[2px]" />
        )}
      </Fragment>
    );
  });

  return (
    <>
      <Header />
      {!isHomePage(params.slug) && !isTablet && (
        <SecondaryMenu menuItems={MENU_ITEMS} />
      )}
      <main
        className={`min-h-screen bg-base-100 px-4 xl:px-8 ${isHomePage(params.slug) || isMobile ? 'pt-[72px]' : 'pt-[118px]'}`}
      >
        {data.modules.map((module: any) => (
          <GeneralFullWidthComponent key={module._key} {...module} />
        ))}
        <section
          id="content-wrapper"
          className="max-w-8xl grid grid-cols-4 gap-4 mx-auto mb-10 sm:mb-20"
        >
          {!isTablet && sidebarItems.length > 0 && (
            <div id="sidebar" className="mt-4 col-span-0 md:col-span-1">
              <Sidebar title={Title} sidebarItems={sidebarItems} />
            </div>
          )}
          <div id="content" className="col-span-4 xl:col-span-3">
            {renderedModules}
          </div>
        </section>
        <section className=" bg-lightGray py-8 xl:py-12 max-w-8xl px-4 xl:px-0 grid grid-cols-1 xl:grid-cols-4 gap-4 mx-auto">
          <div className="col-span-1 xl:col-span-3 xl:col-start-2">
            <ModuleCarousel {...moduleData} />
          </div>
        </section>
        {data.relatedNewsAndEvents && data.relatedNewsAndEvents.length > 0 && (
          <section
            id="whats-new"
            className="grid grid-cols-1 xl:grid-cols-4 mt-12 max-w-8xl px-4 xl:px-0 mx-auto"
          >
            <div className="col-span-1 xl:col-span-3 xl:col-start-2">
              <RelatedArticle newsAndEvents={data.relatedNewsAndEvents} />
            </div>
          </section>
        )}
      </main>
      <Footer />
    </>
  );
}

export default DigitalWorkplacePage;
